import type { CitationResponse } from './api'

/**
 * Mirrors src/answer_service/adapters/driver/teams/cards/factories/answer_card_factory.py.
 * Same regex shapes, same Norwegian labels — kept intentionally close so the SPA renders
 * answers the way Teams users are used to seeing them.
 */

const STALE_THRESHOLD_DAYS = 1825
const STALE_MESSAGE = 'Kilden er eldre enn 5 år'

const WEBSCRAPING_LABELS: Record<string, string> = {
  orto: '(Ortopediteknikk)',
  nav: '(NAV)',
}

const CITATION_CLUSTER_RE = /(?:\[\d+\])+/g
const CITATION_COMMA_LIST_RE = /\[\s*\d+(?:\s*,\s*\d+)*\s*\]/g
const CITATION_NUMBER_RE = /\[(\d+)\]/g
const NUMBER_RE = /\d+/g

const uniqSorted = (raw: readonly string[]): number[] => {
  const set = new Set<number>()
  for (const m of raw) set.add(Number.parseInt(m, 10))
  return [...set].sort((a, b) => a - b)
}

export function consolidateCitations(text: string): string {
  const cluster = (match: string): string => {
    const innerNumbers = (match.match(CITATION_NUMBER_RE) ?? []).map((m) => m.slice(1, -1))
    const numbers = uniqSorted(innerNumbers)
    return numbers.length === 1 ? `[${numbers[0]}]` : `[${numbers.join(', ')}]`
  }
  const commaList = (match: string): string => {
    const numbers = uniqSorted(match.match(NUMBER_RE) ?? [])
    return numbers.length === 1 ? `[${numbers[0]}]` : `[${numbers.join(', ')}]`
  }
  const phase1 = text.replace(CITATION_CLUSTER_RE, cluster)
  return phase1.replace(CITATION_COMMA_LIST_RE, commaList)
}

/**
 * Escape the opening bracket of citation markers so the markdown pass renders
 * them as literal text. Without this, a marker immediately followed by `(...)`
 * — e.g. `[1](https://…)` or even `[1](note)` — parses as a markdown link,
 * swallowing the citation number and emitting a stray anchor. Escaping `[` to
 * `\[` makes remark treat it as a literal bracket, never a link reference.
 *
 * Run this AFTER consolidateCitations, on text destined for <ReactMarkdown>.
 * Matches the consolidated shapes `[1]` and `[1, 2, 3]`.
 */
const CITATION_MARKER_RE = /\[\s*\d+(?:\s*,\s*\d+)*\s*\]/g

export function escapeCitationMarkers(text: string): string {
  return text.replace(CITATION_MARKER_RE, (match) => `\\${match}`)
}

export function sourceLabel(citation: CitationResponse): string {
  if (citation.sourceType === 'sharepoint') return '(SharePoint)'
  if (citation.sourceType === 'tqm') return '(TQM)'
  if (citation.sourceType === 'webscraping') {
    return WEBSCRAPING_LABELS[citation.scrapingSource.toLowerCase()] ?? ''
  }
  return ''
}

export type StaleStatus = { stale: false } | { stale: true; message: string }

export function staleStatus(citation: CitationResponse, now: Date = new Date()): StaleStatus {
  if (citation.sourceType !== 'sharepoint') return { stale: false }
  const modified = parseModifiedAt(citation.modifiedAt)
  if (modified === null) return { stale: false }
  const days = Math.floor((now.getTime() - modified.getTime()) / (1000 * 60 * 60 * 24))
  if (days <= STALE_THRESHOLD_DAYS) return { stale: false }
  return { stale: true, message: STALE_MESSAGE }
}

function parseModifiedAt(raw: string): Date | null {
  if (!raw) return null
  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function citationDisplayName(citation: CitationResponse): string {
  const name = citation.documentName || 'Ukjent kilde'
  const label = sourceLabel(citation)
  return label ? `${name} ${label}` : name
}
